/** CreateCapabilityModal — form modal for creating a new AI capability. */

import { useMemo, useState, type FormEvent } from "react";
import { Modal, Input, Select, Radio, message } from "antd";
import { useCreateCapability, useEngineCatalog } from "../../api/queries";
import { PRACTICE_AREAS } from "../../types/capability";
import type { CapabilityType, CapabilityScope } from "../../types/capability";
import { PRACTICE_AREA_LABELS, SCOPE_LABELS, CAPABILITY_TYPE_LABELS } from "../../types/capability-labels";

// 可手工创建的能力类型；mcp_tool 由 MCP server 同步产生，不在此手工新建
const CREATABLE_TYPES: CapabilityType[] = ["skill", "agent", "playbook"];
import formStyles from "../../styles/form-modal.module.css";

// ---------------------------------------------------------------------------
// Practice area display labels
// ---------------------------------------------------------------------------

const PRACTICE_AREA_OPTIONS = PRACTICE_AREAS.map((area) => ({
  value: area,
  label: PRACTICE_AREA_LABELS[area] ?? area,
}));

// ---------------------------------------------------------------------------
// Scope labels
// ---------------------------------------------------------------------------

const SCOPE_OPTIONS: { value: CapabilityScope; label: string }[] = (
  Object.entries(SCOPE_LABELS) as [CapabilityScope, string][]
).map(([value, label]) => ({ value, label }));

// ---------------------------------------------------------------------------
// Form state
// ---------------------------------------------------------------------------

interface CreateForm {
  title: string;
  description: string;
  type: CapabilityType;
  practiceAreas: string[];
  tags: string[];
  scope: CapabilityScope;
  scopeRef: string;
  content: string;
  // 任务二阶段二：绑定引擎目标（agent 名 / skill id）。空串 = 不绑定（存量
  // 手工卡行为）；类型切换时清空——agent 名与 skill id 是两个命名空间。
  engineRefName: string;
}

const INITIAL_FORM: CreateForm = {
  title: "",
  description: "",
  type: "skill",
  practiceAreas: [],
  tags: [],
  scope: "personal",
  scopeRef: "",
  content: "",
  engineRefName: "",
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface CreateCapabilityModalProps {
  open: boolean;
  onClose: () => void;
}

export function CreateCapabilityModal({
  open,
  onClose,
}: CreateCapabilityModalProps) {
  const [form, setForm] = useState<CreateForm>({ ...INITIAL_FORM });
  const [messageApi, contextHolder] = message.useMessage();
  const createMutation = useCreateCapability();

  // 任务二阶段二：绑定选择器数据源。仅 skill/agent 类型可绑定（playbook 后端
  // 拒绝），目录请求也只在弹窗打开且类型可绑定时发出。
  const bindableType = form.type === "skill" || form.type === "agent";
  const { data: catalog, isError: catalogError } = useEngineCatalog({
    enabled: open && bindableType,
  });
  const bindingOptions = useMemo(() => {
    if (form.type === "agent") {
      return (catalog?.agents ?? []).map((a) => ({
        value: a.name,
        label: `${a.displayName}（${a.name}）`,
      }));
    }
    if (form.type === "skill") {
      return (catalog?.skills ?? []).map((s) => ({
        value: s.id,
        label: `${s.name}（${s.id}）`,
      }));
    }
    return [];
  }, [form.type, catalog]);

  const updateField = <K extends keyof CreateForm>(
    key: K,
    value: CreateForm[K],
  ) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  // 类型切换时清空绑定选择——agent 名与 skill id 分属两个注册表，跨类型
  // 残留的选中值提交后必然被引擎核验拒绝。
  const handleTypeChange = (type: CapabilityType) => {
    setForm((prev) => ({ ...prev, type, engineRefName: "" }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!form.title.trim()) {
      messageApi.warning("请输入能力名称");
      return;
    }

    if (!form.description.trim()) {
      messageApi.warning("请输入能力描述");
      return;
    }

    try {
      await createMutation.mutateAsync({
        title: form.title.trim(),
        description: form.description.trim(),
        type: form.type,
        practiceAreas:
          form.practiceAreas.length > 0 ? form.practiceAreas : undefined,
        tags: form.tags.length > 0 ? form.tags : undefined,
        scope: form.scope,
        content: form.content.trim() || undefined,
        engineRefName: form.engineRefName || undefined,
      });

      messageApi.success("能力创建成功");
      setForm({ ...INITIAL_FORM });
      onClose();
    } catch {
      messageApi.error("创建失败，请稍后重试");
    }
  };

  const handleClose = () => {
    setForm({ ...INITIAL_FORM, scopeRef: "" });
    onClose();
  };

  return (
    <>
      {contextHolder}
      <Modal
        title="创建 AI 能力"
        open={open}
        onCancel={handleClose}
        onOk={handleSubmit}
        okText="创建"
        cancelText="取消"
        width={560}
        rootClassName="jerryCreateCapabilityModal"
        okButtonProps={{
          className: "",
          loading: createMutation.isPending,
        }}
        cancelButtonProps={{ className: "" }}
        destroyOnHidden
      >
        <form onSubmit={handleSubmit}>
          {/* Name */}
          <div className={formStyles.formField}>
            <label className={formStyles.formLabel}>
              能力名称 <span className={formStyles.formRequired}>*</span>
            </label>
            <Input
              value={form.title}
              onChange={(e) => updateField("title", e.target.value)}
              placeholder="输入能力名称"
              maxLength={100}
            />
          </div>

          {/* Description */}
          <div className={formStyles.formField}>
            <label className={formStyles.formLabel}>
              描述 <span className={formStyles.formRequired}>*</span>
            </label>
            <Input.TextArea
              value={form.description}
              onChange={(e) => updateField("description", e.target.value)}
              placeholder="输入能力描述"
              rows={3}
              maxLength={500}
            />
          </div>

          {/* Type selector */}
          <div className={formStyles.formField}>
            <label className={formStyles.formLabel}>类型</label>
            <Radio.Group
              value={form.type}
              onChange={(e) => handleTypeChange(e.target.value)}
            >
              {CREATABLE_TYPES.map((t) => (
                <Radio key={t} value={t}>
                  {CAPABILITY_TYPE_LABELS[t]}
                </Radio>
              ))}
            </Radio.Group>
          </div>

          {/* 任务二阶段二：绑定引擎目标。绑定后「调用此能力」直接以引擎侧
              目标就位（agent 指定顾问 / skill 挂载技能），不再只靠预填文本。 */}
          {bindableType && (
            <div className={formStyles.formField}>
              <label className={formStyles.formLabel}>绑定引擎目标</label>
              <Select
                value={form.engineRefName || undefined}
                onChange={(v) => updateField("engineRefName", v ?? "")}
                options={bindingOptions}
                placeholder="选择可绑定的引擎目标（可选）"
                allowClear
                showSearch
                optionFilterProp="label"
                disabled={catalogError}
                data-testid="create-capability-engine-ref"
              />
              {catalogError && (
                <div
                  className={formStyles.formHint}
                  data-testid="create-capability-engine-ref-error"
                >
                  引擎目录暂不可用，可先创建，稍后在编辑中补绑。
                </div>
              )}
            </div>
          )}

          {/* Practice Areas */}
          <div className={formStyles.formField}>
            <label className={formStyles.formLabel}>业务领域</label>
            <Select
              mode="multiple"
              value={form.practiceAreas}
              onChange={(v) => updateField("practiceAreas", v)}
              options={PRACTICE_AREA_OPTIONS}
              placeholder="选择业务领域"
              allowClear
            />
          </div>

          {/* Tags */}
          <div className={formStyles.formField}>
            <label className={formStyles.formLabel}>标签</label>
            <Select
              mode="tags"
              value={form.tags}
              onChange={(v) => updateField("tags", v)}
              placeholder="输入标签后回车"
              tokenSeparators={[","]}
              open={false}
            />
          </div>

          {/* Scope */}
          <div className={formStyles.formField}>
            <label className={formStyles.formLabel}>可见范围</label>
            <Radio.Group
              value={form.scope}
              onChange={(e) => updateField("scope", e.target.value)}
            >
              {SCOPE_OPTIONS.map((opt) => (
                <Radio key={opt.value} value={opt.value}>
                  {opt.label}
                </Radio>
              ))}
            </Radio.Group>
          </div>

          {/* Content (Markdown textarea) */}
          <div className={formStyles.formField}>
            <label className={formStyles.formLabel}>Prompt 内容</label>
            <Input.TextArea
              value={form.content}
              onChange={(e) => updateField("content", e.target.value)}
              placeholder="输入 Prompt 内容（支持 Markdown 格式）"
              rows={6}
            />
          </div>
        </form>
      </Modal>
    </>
  );
}
